import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { useCreateApInvoice, type ApInvoiceCreateManual } from '../hooks/useApInvoices';
import { CURRENCIES } from '../lib/catalogs';

const TAX_RATES = [0, 8, 10] as const;

interface DraftLine {
  description: string;
  qty: string;
  unitPriceCents: string;
  taxRate: number;
}

function emptyLine(): DraftLine {
  return { description: '', qty: '1', unitPriceCents: '', taxRate: 10 };
}

export function ApInvoiceCreateDrawer({
  wsId,
  open,
  onOpenChange,
  onCreated,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateApInvoice(wsId);

  const today = new Date().toISOString().slice(0, 10);
  const in30 = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();

  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(in30);
  const [currency, setCurrency] = useState('JPY');
  const [jpRegistration, setJpRegistration] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setVendorName('');
    setInvoiceNumber('');
    setIssueDate(today);
    setDueDate(in30);
    setCurrency('JPY');
    setJpRegistration('');
    setNotes('');
    setLines([emptyLine()]);
    setError(null);
  };

  const subtotal = lines.reduce((s, l) => {
    const q = Number(l.qty);
    const u = Number(l.unitPriceCents);
    return Number.isFinite(q) && Number.isFinite(u) ? s + Math.round(q * u) : s;
  }, 0);
  const tax = lines.reduce((s, l) => {
    const q = Number(l.qty);
    const u = Number(l.unitPriceCents);
    return Number.isFinite(q) && Number.isFinite(u) ? s + Math.round((q * u * l.taxRate) / 100) : s;
  }, 0);
  const total = subtotal + tax;

  const onSubmit = async () => {
    setError(null);
    const lineItems: ApInvoiceCreateManual['lineItems'] = [];
    for (const l of lines) {
      if (!l.description.trim()) continue;
      const qty = Number(l.qty);
      const unit = Number(l.unitPriceCents);
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(unit) || unit < 0) {
        setError(t('invoicesAr.errorLineInvalid'));
        return;
      }
      lineItems.push({
        description: l.description.trim(),
        qty,
        unitPriceCents: unit,
        taxRate: l.taxRate,
        amountCents: Math.round(qty * unit),
      });
    }
    try {
      const created = await create.mutateAsync({
        invoiceNumber: invoiceNumber.trim() || undefined,
        issueDate: issueDate || undefined,
        dueDate: dueDate || undefined,
        currency,
        subtotalCents: subtotal,
        taxCents: tax,
        totalCents: total,
        jpInvoiceRegistration: jpRegistration.trim() || undefined,
        vendorName: vendorName.trim() || undefined,
        notes: notes.trim() || undefined,
        lineItems: lineItems.length ? lineItems : undefined,
      });
      reset();
      onCreated?.(created.id);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Create failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      size="lg"
      title={t('invoicesAp.createInvoice')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('invoicesAr.create')}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}

        <section className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
          <Field label={t('inbox.field.vendor')}>
            <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} className={inputCn} />
          </Field>
          <Field label={t('inbox.field.invoiceNumber')}>
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="INV-2026-04-001"
              className={inputCn}
            />
          </Field>
          <Field label={t('inbox.field.issueDate')}>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className={inputCn}
            />
          </Field>
          <Field label={t('inbox.field.dueDate')}>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={inputCn}
            />
          </Field>
          <Field label={t('inbox.field.currency')}>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.symbol}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('inbox.field.registration')}>
            <input
              value={jpRegistration}
              onChange={(e) => setJpRegistration(e.target.value.toUpperCase())}
              placeholder="T1234567890123"
              className={inputCn}
            />
          </Field>
          <div className="col-span-2">
            <Field label={t('inbox.field.notes')}>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCn} />
            </Field>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wide text-ink-500">{t('inbox.lineItems')}</h3>
            <button
              type="button"
              onClick={() => setLines([...lines, emptyLine()])}
              className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
            >
              <Plus size={12} />
              {t('invoicesAr.addLine')}
            </button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 items-start p-2 rounded-lg border border-ink-200 dark:border-ink-800"
              >
                <input
                  value={l.description}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, description: e.target.value } : ln)))
                  }
                  placeholder={t('inbox.col.description')}
                  className={`${inputCn} col-span-5`}
                />
                <input
                  type="number"
                  value={l.qty}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, qty: e.target.value } : ln)))
                  }
                  placeholder={t('inbox.col.qty')}
                  className={`${inputCn} col-span-1 text-right`}
                />
                <input
                  type="number"
                  value={l.unitPriceCents}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, unitPriceCents: e.target.value } : ln)))
                  }
                  placeholder={t('invoicesAr.unitCents')}
                  className={`${inputCn} col-span-3 text-right`}
                />
                <select
                  value={l.taxRate}
                  onChange={(e) =>
                    setLines(
                      lines.map((ln, idx) => (idx === i ? { ...ln, taxRate: Number(e.target.value) } : ln)),
                    )
                  }
                  className={`${inputCn} col-span-2`}
                >
                  {TAX_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines(lines.filter((_, idx) => idx !== i))}
                  disabled={lines.length === 1}
                  className="col-span-1 p-1.5 rounded-md text-ink-500 hover:text-warn-600 hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-30 disabled:pointer-events-none"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 bg-ink-50 dark:bg-ink-900/50">
          <div className="grid grid-cols-3 gap-x-4 text-xs">
            <div>
              <div className="uppercase tracking-wide text-ink-500">{t('inbox.field.subtotal')}</div>
              <div className="num mt-0.5">
                {subtotal.toLocaleString()} {currency}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wide text-ink-500">{t('inbox.field.tax')}</div>
              <div className="num mt-0.5">
                {tax.toLocaleString()} {currency}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wide text-ink-500">{t('inbox.field.total')}</div>
              <div className="num text-base font-semibold mt-0.5">
                {total.toLocaleString()} {currency}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Drawer>
  );
}

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
